import request from '@/utils/request'
import { isV2CommonApiOn } from '@/services/feature/v2CommonApi'
import { LoginUserInfo, SignUpReq } from '@/services/auth/login.types'
import { MenuGrid, MenuListReq, MenuListRes, MenuTreeRes } from '@/services/auth/menu.types'
import { GRID_COLUMNS_API_PATH, MENU_TYPE } from '@/services/auth/consts'
import { GridColumnState } from '@/services/auth/gridColumn.types'
import { Utils } from '@/utils/Utils'

// LOGISPRD-14497 — feature.v2CommonApi.authCheck ON 이면 2.0(`GET /api/v1/auth/me`, 1.0 과 같은 키)을 부른다. OFF·flag 조회 실패 = 1.0.
export const callToCheckLoginUser = async (config?: { skipOktaReauth?: boolean }) => {
  if (!(await isV2CommonApiOn('authCheck', config ?? {}))) return request.get<LoginUserInfo>(`/api/auth/check`, undefined, config)

  const { data } = await request.get<ApiResponseV2<LoginUserInfo>>(`/api/v1/auth/me`, undefined, config)
  // 빈 data 는 1.0 의 빈 응답(request.get reject)처럼 실패로 본다 — 콜백이 "Okta 로그인 처리 오류" 로 처리한다.
  if (!data) throw new Error('empty')
  return data
}

// LOGISPRD-9448 — 비활성 계정 활성화 요청 (moms-1.0 `POST /api/auth/request-activation`, HANDOFF §10.3).
// 요청 바디 없음 — 신원은 게이트웨이가 Okta Bearer(JWKS 검증) 에서 추출해 주입하는 email 이 권위(noAuth 미지정 →
// request.getOptions 가 authMode='okta' 일 때 Authorization: Bearer <oktaToken> 을 실어 보냄. callToCheckLoginUser 와 동일 util 경로).
// 응답은 200(성공/멱등 동일)이며 FE 는 바디를 소비하지 않고 성공만 보고 PENDING 으로 전환한다.
// LOGISPRD-14497 — authCheck ON 이면 2.0(`POST /api/v1/auth/request-activation`). 같은 흐름이라 같은 필드로 켠다.
export const callToRequestActivation = async () =>
  request.post<ApiResponseV2<{ message?: string }>>(
    (await isV2CommonApiOn('authCheck')) ? `/api/v1/auth/request-activation` : `/api/auth/request-activation`,
    {},
  )

// LOGISPRD-8907 — 신규 Okta 회원가입 등록 API (moms-be 2.0). 비밀번호는 받지 않음(Okta 초대로 사용자가 최초 설정).
export const callToSignUp = (params: SignUpReq) =>
  request.post<ApiResponseV2<{ message: string }>>(
    `/api/v1/auth/register`,
    {
      name: params.nickname,
      email: params.email,
      shipperCode: params.shipper_code,
      departmentName: params.DEPT_NM,
      phone: params.phone,
      isPrivacyPolicy: params.IS_PRIVACY_POLICY,
      isSecurityTraining: params.IS_SEC_TRAIN,
      isSecurityAgreement: params.IS_SEC_AGREE,
    },
    { noAuth: true },
  )

// LOGISPRD-15410 — authMenu ON 이면 2.0(`GET /api/v1/auth/me/menus`). 권한 코드·신원은 서버가 정해 화주 코드만 보낸다.
// 2.0 응답은 1.0 모양으로 바꾼다 — MenuStorage 를 거쳐 1.0 iframe 이 자식의 MENU_TREE(부모 코드)로 경로를 만들고,
// MENU_TYP 이 'PROG' 가 아니면 MENU_ITEM 으로 재귀한다(moms-fe-1.0 `store/modules/menu.js` initMenu).
export const callToGetMenuList = async (params: MenuListReq): Promise<MenuListRes> => {
  if (!(await isV2CommonApiOn('authMenu'))) {
    return request.post<MenuListRes, { params: string }>(`/api/auth/menu`, { params: JSON.stringify(params) })
  }

  const { data } = await request.get<ApiResponseV2<{ items: MenuTreeRes[] }>>(`/api/v1/auth/me/menus`, { shipperCode: params.SPR_NM })
  // 빈 data 를 메뉴 0개로 보면 MenuGuard 가 "접근 권한 없음" 으로 오판한다 — 조회 실패로 본다.
  if (!data) throw new Error('empty')
  return data.items.map((parent) => ({
    MENU_CD: parent.menuCode,
    MENU_NM: parent.menuName,
    MENU_TYP: MENU_TYPE.FOLD,
    MENU_TREE: 'root',
    MENU_ITEM: parent.children.map((child) => ({
      MENU_CD: child.menuCode,
      MENU_NM: child.menuName,
      MENU_TYP: MENU_TYPE.PROG,
      MENU_TREE: parent.menuCode,
      allowedButtons: child.allowedButtons,
      toolbar: child.toolbar,
      editable: child.editable,
    })),
  }))
}

// LOGISPRD-17094 — null 이면 BE flag(menuGrids) OFF. menuCode 는 DB 대문자.
export const callToGetMenuGrids = async (menuCode: string, shipperCode: string): Promise<MenuGrid[] | null> => {
  const { data } = await request.get<ApiResponseV2<{ grids: MenuGrid[] | null }>>(`/api/v1/auth/me/menus/${menuCode}/grids`, { shipperCode })
  // 빈 data 를 설정 없음([])으로 보면 툴바가 전부 사라진다 — 조회 실패로 보고 지금 동작에 맡긴다.
  if (!data) throw new Error('empty')
  return data.grids
}

// LOGISPRD-15456 — 개인 컬럼 배치(BE LOGISPRD-15455). 사용자는 토큰, 화주는 shipperCode 로 서버가 나눈다. 저장값이 없으면 items 는 [].
export const callToGetGridColumns = async (gridKey: string, shipperCode: string) => {
  const { data } = await request.get<ApiResponseV2<{ items: GridColumnState[] }>>(`${GRID_COLUMNS_API_PATH}/${gridKey}`, { shipperCode })
  // 빈 data 를 저장값 없음으로 보면 legacy 그리드가 localStorage 배치로 서버 저장값을 덮어쓴다 — 조회 실패로 본다.
  if (!data) throw new Error('empty')
  return data.items
}

// 통째로 교체한다. [] 를 보내면 저장값을 지운다.
export const callToSaveGridColumns = (gridKey: string, shipperCode: string, items: GridColumnState[]) =>
  request.put<ApiResponseV2<null>, GridColumnState[]>(`${GRID_COLUMNS_API_PATH}/${gridKey}${Utils.qsStringify({ shipperCode })}`, items)
